import React, { useState, useMemo } from "react";
import {
  Alert,
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell,
  TableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  CircularProgress,
  Badge,
  IconButton,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloseIcon from "@mui/icons-material/Close";
import BackBtn from "../widgets/BackBtn";
import useApiEp from "../hooks/useApiEp";
import MuPb from "../widgets/MuPb";
import { getErrorResponse } from "../utils/util";

// --- UTILITY FUNCTIONS FOR EMAIL GENERATION ---

const DEFAULT_DP = "https://via.placeholder.com/50";
const TimeZoneIndia = { timeZone: "Asia/Kolkata" };

export function getCatalogueTemplate(user, supplier, headerUrl, footerUrl) {
  const groupedCategories = (user.productCategories || []).reduce(
    (acc, { mainCat, subCat, subSubCat }) => {
      if (!acc[mainCat]) acc[mainCat] = {};
      if (!acc[mainCat][subCat]) acc[mainCat][subCat] = [];
      acc[mainCat][subCat].push(subSubCat);
      return acc;
    },
    {}
  );

  const formattedCategories = Object.entries(groupedCategories)
    .map(([mainCat, subCats]) => {
      const subCatHTML = Object.entries(subCats)
        .map(
          ([subCat, subSubCats]) =>
            `<em>${subCat}:</em> ${subSubCats.join(", ")}`
        )
        .join("; ");
      return `<strong>${mainCat}:</strong> ${subCatHTML}`;
    })
    .join("<br>");

  const sanitize = (input) => (input ? String(input).replace(/[<>]/g, "") : "");

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta http-equiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Catalogue</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; background-color: #f5f5f5; margin: 0; padding: 0; line-height: 1.6; color: #333; }
          .container { max-width: 800px; margin: 20px auto; padding: 24px; background-color: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
          .section { border: 1px solid #e0e0e0; padding: 16px; margin: 16px 0; border-radius: 6px; }
          .profile-image { width: 100px; max-width: 100%; display: block; margin: 16px auto; border-radius: 50%; }
          table { width: 100%; border-collapse: collapse; margin: 24px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e0e0e0; }
          th { background-color: #f8f8f8; font-weight: 600; }
          a { color: #0066cc; text-decoration: none; }
          a:hover { text-decoration: underline; }
          img.header-footer { width: 100%; max-width: 800px; height: auto; display: block; margin: 0 auto; border-radius: 6px; }
        </style>
      </head>
      <body>
        <div class="container">
          <img src="${sanitize(
            headerUrl
          )}" alt="Header Image" class="header-footer" />
          <div class="section">
            <p><strong>From:</strong> Company: ${sanitize(
              user.companyName
            )}, Name: ${sanitize(user.contactSalutation)} ${sanitize(
    user.contactFirstName
  )} ${sanitize(user.contactLastName)}, Phone: ${sanitize(
    user.mobile
  )}, Email: ${sanitize(user.contactEmail)}</p>
          </div>
          <div class="section">
            <p><strong>To:</strong> Company: ${sanitize(
              supplier.supplierName
            )},<br />Address: ${sanitize(supplier.address)}</p>
            <p>Phone: ${sanitize(supplier.phoneNumber)}</p>
            <p>Email: ${sanitize((supplier.emails || []).join(", "))}</p>
          </div>
          ${
            user.profile_image
              ? `<img src="${sanitize(
                  user.profile_image
                )}" alt="Profile Image" class="profile-image" />`
              : ""
          }
          <table>
            <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
            <tbody>
              <tr><td><strong>Company Name:</strong></td><td>${sanitize(
                user.companyName
              )}</td></tr>
              <tr><td><strong>Product Categories:</strong></td><td>${formattedCategories}</td></tr>
            </tbody>
          </table>
          <img src="${sanitize(
            footerUrl
          )}" alt="Footer Image" class="header-footer" />
        </div>
      </body>
    </html>
  `.trim();
}

export function getExhibitorInfoOes(user, titleString) {
  function containsSetupAndDismantling(name) {
    const regex = /setup/i;
    const regexDismantling = /dismantling/i;
    return regex.test(name) && regexDismantling.test(name);
  }

  const shouldShowDates = containsSetupAndDismantling(titleString);

  return `<table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
    <tr><th style="padding: 10px; border-bottom: 1px solid #ddd; background-color: #f2f2f2; text-align:left;">Attribute</th><th style="padding: 10px; border-bottom: 1px solid #ddd; background-color: #f2f2f2; text-align:left;">Value</th></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Company Name:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.companyName?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Raw Space:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.spaceType?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Stand Size:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.standSize?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Hall Number:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.hallNumber?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Stand Number:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.standNumber?.value || "N/A"
    }</td></tr>
    ${
      shouldShowDates
        ? `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Exhibits Arrival Date:</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;">${
            user.exhibitArrival?.value
              ? new Date(user.exhibitArrival.value).toLocaleDateString(
                  "en-IN",
                  TimeZoneIndia
                )
              : "N/A"
          }</td>
        </tr>
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Material Arrival Date:</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;">${
            user.materialArrival?.value
              ? new Date(user.materialArrival.value).toLocaleDateString(
                  "en-IN",
                  TimeZoneIndia
                )
              : "N/A"
          }</td>
        </tr>
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Dismantling Date:</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;">${
            user.dismantling?.value
              ? new Date(user.dismantling.value).toLocaleDateString(
                  "en-IN",
                  TimeZoneIndia
                )
              : "N/A"
          }</td>
        </tr>
    `
        : ""
    }
  </table>`;
}

export function oesTemplate(
  user,
  templateBody = "",
  supplier,
  formTitle = "",
  headerUrl,
  footerUrl
) {
  return `<!DOCTYPE html>
  <html lang="en">
  <head>
      <meta charset="UTF-8" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Email Template</title>
      <style>
          body { font-family: Arial, sans-serif; background-color: #f4f4f4; margin: 0; padding: 0; width: 100%; -webkit-text-size-adjust: none; }
          .container { max-width: 800px; margin: 0 auto; padding: 20px; background-color: #ffffff; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f2f2f2; }
      </style>
  </head>
  <body>
      <div class="container">
          ${
            headerUrl
              ? `<img src="${headerUrl}" alt="Header Image" style="display: block; margin: 0 auto; width: 100%;" />`
              : ""
          }
          <div style="border: 1px solid #000; padding: 10px; margin: 10px;">
              <p> <strong>From:</strong> Company: ${
                user.companyName?.value || "N/A"
              }, Name: ${user.contactSalutation?.value || ""} ${
    user.contactFirstName?.value || ""
  } ${user.contactLastName?.value || ""}, Phone: ${
    user.mobile?.value || "N/A"
  }, Email: ${user.contactEmail?.value || "N/A"} </p>
          </div>
          <div style="border: 1px solid #000; padding: 10px; margin: 10px;">
              <p> <strong>To:</strong> Company: ${
                supplier.supplierName || "N/A"
              },<br /> Address: ${supplier.address || "N/A"} </p>
              <p>Phone number: ${supplier.phoneNumber || "N/A"}</p>
              <p>Email: ${supplier.emails?.join(",") || "N/A"}</p>
          </div> 
          ${getExhibitorInfoOes(user, formTitle)}<br />
          ${templateBody}<br />
          ${
            footerUrl
              ? `<img src="${footerUrl}" alt="Footer Image" style="display: block; margin: 0 auto; width: 100%;" />`
              : ""
          }
      </div>
  </body>
  </html>`;
}

function subTotal(rowIndex, headerIndex, myItems, headers) {
  const formulaString = headers[headerIndex]["formula"];
  const suffixes =
    formulaString
      .match(/key(\d+)/g)
      ?.map((match) => match.replace("key", "")) || [];

  const values = [];
  for (const suffix of suffixes) {
    const ci = parseInt(suffix) - 1;
    const type = headers[ci]?.type;
    let value = 0;
    if (type === "input number") {
      value = parseInt(myItems[rowIndex][`value${ci}`]) || 0;
    } else if (type === "label") {
      value = parseInt(myItems[rowIndex][`key${suffix}`]) || 0;
    } else if (type === "input checkbox") {
      value = myItems[rowIndex][`value${ci}`] ? 1 : 0;
    } else if (type === "label checkbox") {
      if (myItems[rowIndex][`value${ci}`]) {
        value =
          parseInt(myItems[rowIndex][`key${suffix}`]?.replace(/,/g, "")) || 0;
      } else {
        value = 0;
      }
    } else if (type === "input date range") {
      if (
        myItems[rowIndex][`dateTo${ci}`] &&
        myItems[rowIndex][`dateFrom${ci}`]
      ) {
        const dateFrom = new Date(myItems[rowIndex][`dateFrom${ci}`]);
        const dateTo = new Date(myItems[rowIndex][`dateTo${ci}`]);
        const diffInDays =
          Math.abs((dateTo - dateFrom) / (1000 * 60 * 60 * 24)) + 1;
        value = diffInDays;
      }
    }
    values.push(value);
  }

  let updatedFormula = formulaString;
  suffixes.forEach((suffix, index) => {
    const keyPattern = new RegExp(`key${suffix}`, "g");
    updatedFormula = updatedFormula.replace(keyPattern, values[index]);
  });

  let total = 0;
  try {
    // eslint-disable-next-line no-eval
    total = eval(updatedFormula);
  } catch (error) {
    console.error("Error evaluating formula:", error);
  }
  return total;
}

function renderCell(header, row, i) {
  let cellHtml = "";
  if (header.type === "label") {
    const value = row[`key${i + 1}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">${value}</td>`;
  } else if (header.type === "label checkbox") {
    const isChecked = row[`value${i}`] ? "checked" : "";
    const label = row[`key${i + 1}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;"><input type="checkbox" ${isChecked} disabled> ${label}</td>`;
  } else if (header.type === "input text" || header.type === "input number") {
    const value = row[`value${i}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">${value}</td>`;
  } else if (header.type === "input date range") {
    const dateFrom = row[`dateFrom${i}`]
      ? new Date(row[`dateFrom${i}`]).toLocaleDateString("en-IN", TimeZoneIndia)
      : "";
    const dateTo = row[`dateTo${i}`]
      ? new Date(row[`dateTo${i}`]).toLocaleDateString("en-IN", TimeZoneIndia)
      : "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">From: ${dateTo}<br>To: ${dateFrom}</td>`;
  } else if (header.type === "input checkbox") {
    const isChecked = row[`value${i}`] ? "checked" : "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;"><input type="checkbox" ${isChecked} disabled></td>`;
  } else if (header.type === "calc") {
    const value = row[`calcVal${i}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">${value}</td>`;
  } else {
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;"></td>`;
  }
  return cellHtml;
}

export function generateRatedItemsHTML(
  headers,
  itemsList,
  isColumnSelection = false,
  selectedColumn = null,
  totalRequired = false
) {
  const columnsWithCheckedItems = new Set();
  if (isColumnSelection && selectedColumn != null) {
    itemsList.forEach((item) => {
      headers.forEach((header, i) => {
        if (
          i !== selectedColumn &&
          (header.type === "input checkbox" ||
            header.type === "label checkbox") &&
          item[`value${i}`]
        ) {
          columnsWithCheckedItems.add(i);
        }
      });
    });
  }

  const itemsWithSubtotal = itemsList.map((item, rowIndex) => {
    const newItem = { ...item };
    if (isColumnSelection && selectedColumn != null) {
      const isChecked = item[`value${selectedColumn}`];
      const valueKey = `key${selectedColumn + 1}`;
      const rawValue = item[valueKey];
      const value = parseFloat(String(rawValue).replace(/,/g, "")) || 0;
      newItem.subtotal = isChecked ? value : 0;
    } else {
      const calcIndex = headers.findIndex((item) => item.type === "calc");
      if (calcIndex !== -1) {
        newItem[`calcVal${calcIndex}`] = subTotal(
          rowIndex,
          calcIndex,
          itemsList,
          headers
        );
        newItem.subtotal = newItem[`calcVal${calcIndex}`] || 0;
      } else {
        newItem.subtotal = 0;
      }
    }
    return newItem;
  });

  const filteredItems = itemsWithSubtotal.filter((item) => {
    if (isColumnSelection && selectedColumn != null) {
      return item[`value${selectedColumn}`];
    } else {
      return item.subtotal > 0;
    }
  });

  const totalValue = totalRequired
    ? filteredItems.reduce((acc, item) => acc + (item.subtotal || 0), 0)
    : 0;

  let html =
    '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; margin-top: 15px;">';
  html += "<thead><tr>";
  headers.forEach((header, i) => {
    if (
      !isColumnSelection ||
      i === selectedColumn ||
      columnsWithCheckedItems.has(i) ||
      (header.type !== "input checkbox" && header.type !== "label checkbox")
    ) {
      html += `<th style="padding: 10px; border: 1px solid #ddd; background-color: #f2f2f2;">${header.header}</th>`;
    }
  });
  if (isColumnSelection && selectedColumn != null) {
    html += `<th style="padding: 10px; border: 1px solid #ddd; background-color: #f2f2f2;">Subtotal</th>`;
  }
  html += "</tr></thead><tbody>";

  filteredItems.forEach((row, rowIndex) => {
    html += "<tr>";
    headers.forEach((header, i) => {
      if (
        !isColumnSelection ||
        i === selectedColumn ||
        columnsWithCheckedItems.has(i) ||
        (header.type !== "input checkbox" && header.type !== "label checkbox")
      ) {
        html += renderCell(header, row, i);
      }
    });
    if (isColumnSelection && selectedColumn != null) {
      html += `<td style="padding: 10px; border: 1px solid #ddd;">${row.subtotal?.toLocaleString()}</td>`;
    }
    html += "</tr>";
  });
  html += "</tbody>";

  if (totalRequired) {
    const colspan = headers.filter(
      (header, i) =>
        !isColumnSelection ||
        i === selectedColumn ||
        columnsWithCheckedItems.has(i) ||
        (header.type !== "input checkbox" && header.type !== "label checkbox")
    ).length;
    const adjustedColspan =
      isColumnSelection && selectedColumn != null ? colspan : colspan - 1;

    html += "<tfoot>";
    html += `<tr><td colspan="${adjustedColspan}" style="text-align: right; padding: 10px; border: 1px solid #ddd;">Total</td><td style="padding: 10px; border: 1px solid #ddd;">${totalValue?.toLocaleString()}</td></tr>`;
    const gst = Math.round(totalValue * 0.18);
    html += `<tr><td colspan="${adjustedColspan}" style="text-align: right; padding: 10px; border: 1px solid #ddd;">18% GST</td><td style="padding: 10px; border: 1px solid #ddd;">${gst?.toLocaleString()}</td></tr>`;
    const grandTotal = Math.round(totalValue + gst);
    html += `<tr><td colspan="${adjustedColspan}" style="text-align: right; padding: 10px; border: 1px solid #ddd;"><strong>Grand Total</strong></td><td style="padding: 10px; border: 1px solid #ddd;"><strong>${grandTotal?.toLocaleString()}</strong></td></tr>`;
    html += "</tfoot>";
  }

  html += "</table>";
  return html;
}

export function generateMembersTableHTML(members) {
  let html = `
    <table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; margin-top: 15px;">
      <thead>
        <tr style="background-color: #f2f2f2; font-weight: bold;">
          <th style="padding: 10px; border: 1px solid #ddd;">Profile Image</th>
          <th style="padding: 10px; border: 1px solid #ddd;">First Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Last Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Phone</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Email</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Role</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Designation</th>
        </tr>
      </thead>
      <tbody>
  `;
  (members || []).forEach((member) => {
    const profileImage = member.profile_image || DEFAULT_DP;
    html += `
      <tr>
        <td style="text-align: center; padding: 10px; border: 1px solid #ddd;">
          <img src="${profileImage}" alt="Profile Image" style="width: 50px; height: 50px; border-radius: 50%; display: block; margin: 0 auto;" />
        </td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.firstName || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.lastName || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.phone || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.email || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.role || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.designation || ""
        }</td>
      </tr>
    `;
  });
  html += `</tbody></table>`;
  return html;
}

export function generateBadgeEntitlementHTML(properties, members, user) {
  const {
    basenum = 0,
    basesqm = 0,
    extranum = 0,
    extrasqm = 1,
  } = properties || {};
  const intBasenum = parseInt(basenum, 10) || 0;
  const intBasesqm = parseInt(basesqm, 10) || 0;
  const intExtranum = parseInt(extranum, 10) || 0;
  const intExtrasqm = parseInt(extrasqm, 10) || 1;

  const mySize = parseFloat(user?.standSize?.value || 0);
  const totalFreeBadges =
    intBasenum +
    Math.ceil(Math.max(0, mySize - intBasesqm) * (intExtranum / intExtrasqm));
  const totalMembers = (members || []).length;
  const paidBadges = Math.max(0, totalMembers - totalFreeBadges);

  return `
    <div style="max-width: 500px; margin: 15px auto; font-family: Arial, sans-serif;">
      <div style="border: 1px solid #ddd; padding: 16px; border-radius: 8px; background-color: #f5f5f5;">
        <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
          <tbody>
            <tr>
              <td style="padding: 8px; color: #555;">Total Free Badges:</td>
              <td style="padding: 8px; text-align: right; color: #007bff; font-weight: bold;">${totalFreeBadges}</td>
            </tr>
            <tr>
              <td style="padding: 8px; color: #555;">Paid Badges Required:</td>
              <td style="padding: 8px; text-align: right; color: #ff4c4c; font-weight: bold;">${paidBadges}</td>
            </tr>
            <tr>
              <td style="padding: 8px; color: #555;">Total Members:</td>
              <td style="padding: 8px; text-align: right; color: #333; font-weight: bold;">${totalMembers}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
}

export function generateCartTemplate(cartItems) {
  let subtotal = 0;
  let itemRows = "";

  (cartItems || []).forEach((item) => {
    const itemTotal = (item.price || 0) * (item.quantity || 0);
    subtotal += itemTotal;
    itemRows += `
      <tr>
        <td style="border: 1px solid #ddd; padding: 8px;">${
          item.name || ""
        }</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${
          item.quantity || 0
        }</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">₹${(
          item.price || 0
        ).toFixed(2)}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">₹${itemTotal.toFixed(
          2
        )}</td>
      </tr>
    `;
  });

  const gst = subtotal * 0.18;
  const grandTotal = subtotal + gst;

  return `
      <h2 style="margin-top: 20px;">Order Summary</h2>
      <table style="border-collapse: collapse; width: 100%; max-width: 600px;">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Item Name</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Quantity</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Price (₹)</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Total (₹)</th>
          </tr>
        </thead>
        <tbody>
          ${itemRows}
        </tbody>
      </table>
      <p style="margin-top: 20px; font-size: 16px;"><strong>Subtotal:</strong> ₹${subtotal.toFixed(
        2
      )}</p>
      <p style="font-size: 16px;"><strong>GST (18%):</strong> ₹${gst.toFixed(
        2
      )}</p>
      <p style="font-size: 18px;"><strong>Grand Total:</strong> ₹${grandTotal.toFixed(
        2
      )}</p>
  `;
}

// --- FRONTEND EMAIL PREVIEW GENERATOR ---
const generateEmailPreview = (submission, headerUrl, footerUrl) => {
  const supplier = submission.selectedSupplier || {};

  // Use the actual exhibitor data passed from the submission, fallback to name only if missing
  const realUser = submission.exhibitorData || {
    companyName: { value: submission.exhibitorName || "Exhibitor Company" },
  };

  const mockMembers = []; // Replace with actual members later if needed

  let myTemplate = `<table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; margin-top: 20px;">
    <thead><tr></tr></thead><tbody>`;

  if (submission.form && Array.isArray(submission.form)) {
    submission.form.forEach((form) => {
      if (form.type === "DynamicTable") {
        const { properties } = form;
        let tableHtml = `<tr><td colspan="2"><table style="width: 100%; border-collapse: collapse;"><thead><tr>`;
        properties.headers?.forEach((header) => {
          tableHtml += `<th style="border: 1px solid #ddd; padding: 8px; background-color: #f4f4f4; text-align: left;">${header.header}</th>`;
        });
        tableHtml += `</tr></thead><tbody>`;
        properties.value?.forEach((row) => {
          tableHtml += "<tr>";
          properties.headers?.forEach((header) => {
            const cell = row[header.key] !== undefined ? row[header.key] : "";
            tableHtml += `<td style="border: 1px solid #ddd; padding: 8px;">${cell}</td>`;
          });
          tableHtml += "</tr>";
        });
        tableHtml += `</tbody></table></td></tr>`;
        myTemplate += tableHtml;
      } else if (
        [
          "LabelInput",
          "Select",
          "TextArea",
          "TextField",
          "RadioGroup",
        ].includes(form.type)
      ) {
        if (form.properties?.value) {
          myTemplate += `<tr>
            <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${form.properties?.value}</td>
          </tr>`;
        }
      } else if (form.type === "Checkbox") {
        if (form.properties?.checked) {
          myTemplate += `<tr>
            <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
            <td style="padding: 8px; border: 1px solid #ddd;"><input type="checkbox" checked disabled></td>
          </tr>`;
        }
      } else if (form.type === "DatePicker") {
        const val = form.properties?.value
          ? new Date(form.properties.value).toLocaleDateString(
              "en-IN",
              TimeZoneIndia
            )
          : "";
        myTemplate += `<tr>
          <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${val}</td>
        </tr>`;
      } else if (form.type === "TimePicker") {
        const val = form.properties?.value
          ? new Date(form.properties.value).toLocaleTimeString(
              "en-IN",
              TimeZoneIndia
            )
          : "";
        myTemplate += `<tr>
          <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${val}</td>
        </tr>`;
      } else if (form.type === "RatedItems") {
        const { properties } = form;
        myTemplate += `<tr><td colspan="2">${generateRatedItemsHTML(
          properties.headers || [],
          properties.items || [],
          properties.columnSelection,
          properties.selectedColumn,
          properties.showTotal
        )}</td></tr>`;
      } else if (form.type === "TeamMembers") {
        myTemplate += `
          <tr><td colspan="2">${generateBadgeEntitlementHTML(
            form.properties,
            mockMembers,
            realUser
          )}</td></tr>
          <tr><td colspan="2">${generateMembersTableHTML(mockMembers)}</td></tr>
        `;
      } else if (form.type === "PurchaseItems") {
        myTemplate += `<tr><td colspan="2">${generateCartTemplate(
          form.properties?.value || []
        )}</td></tr>`;
      }
    });
  }

  myTemplate += `</tbody></table>`;

  const finalHeaderUrl =
    headerUrl || "https://via.placeholder.com/800x150?text=Event+Header";
  const finalFooterUrl =
    footerUrl || "https://via.placeholder.com/800x100?text=Event+Footer";

  return oesTemplate(
    realUser,
    myTemplate,
    supplier,
    submission.name,
    finalHeaderUrl,
    finalFooterUrl
  );
};

// --- REACT COMPONENT ---
const OesExplorer = () => {
  // Fetch OES Submissions Data containing { exhibitors, oesSubmissions }
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-oes-submissions`
  );

  // Fetch Header/Footer URLs dynamically
  const { data: headerFooterData, isLoading: isHeaderFooterLoading } = useApiEp(
    `/event/visitor-reg-email-header-footer`
  );

  const [filterExhibitor, setFilterExhibitor] = useState("All");
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);

  // Map Names & Analytics logic
  const { filteredData, analytics, uniqueExhibitors } = useMemo(() => {
    if (!data || !data.oesSubmissions) {
      return { filteredData: [], analytics: {}, uniqueExhibitors: [] };
    }

    const { oesSubmissions, exhibitors = [] } = data;

    // Helper to get the full exhibitor object
    const getExhibitor = (id) => {
      return exhibitors.find((e) => e.username === id);
    };

    // Pre-process submissions to include the mapped name AND full exhibitor data
    const mappedSubmissions = oesSubmissions.map((sub) => {
      const exhibitor = getExhibitor(sub.submittedBy);
      return {
        ...sub,
        exhibitorName:
          exhibitor?.companyName?.value ||
          sub.submittedBy ||
          "Unknown Exhibitor",
        exhibitorData: exhibitor || {}, // Attach real exhibitor data here
      };
    });

    const exCounts = {};
    const supCounts = {};
    const exhibitorsSet = new Set();

    mappedSubmissions.forEach((sub) => {
      const exhibitor = sub.exhibitorName;
      const supplier = sub.selectedSupplier?.supplierName || "No Supplier";

      exhibitorsSet.add(exhibitor);

      exCounts[exhibitor] = (exCounts[exhibitor] || 0) + 1;
      supCounts[supplier] = (supCounts[supplier] || 0) + 1;
    });

    const filtered =
      filterExhibitor === "All"
        ? mappedSubmissions
        : mappedSubmissions.filter(
            (sub) => sub.exhibitorName === filterExhibitor
          );

    return {
      filteredData: filtered,
      uniqueExhibitors: Array.from(exhibitorsSet).sort(),
      analytics: {
        total: mappedSubmissions.length,
        exhibitorCounts: exCounts,
        supplierCounts: supCounts,
      },
    };
  }, [data, filterExhibitor]);

  const handleOpenEmailDialog = (submission) => {
    setSelectedSubmission(submission);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedSubmission(null);
  };

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <BackBtn />
          <Typography variant="h4" ml={2} fontWeight="bold">
            OES Submissions Explorer
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          {/* Filter Dropdown */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Filter by Exhibitor</InputLabel>
            <Select
              value={filterExhibitor}
              onChange={(e) => setFilterExhibitor(e.target.value)}
              label="Filter by Exhibitor"
            >
              <MenuItem value="All">
                <em>All Exhibitors</em>
              </MenuItem>
              {uniqueExhibitors.map((exhibitor) => (
                <MenuItem key={exhibitor} value={exhibitor}>
                  {exhibitor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Stats Button Trigger */}
          <Badge badgeContent={analytics.total || 0} color="secondary">
            <Button
              variant="contained"
              color="primary"
              startIcon={<AssessmentIcon />}
              onClick={() => setStatsDialogOpen(true)}
            >
              Analytics
            </Button>
          </Badge>
        </Box>
      </Box>

      {/* Submissions Data Table */}
      <TableContainer component={Paper} elevation={3}>
        <MuiTable>
          <MuiTableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <MuiTableRow>
              <TableCell>
                <strong>Form Name</strong>
              </TableCell>
              <TableCell>
                <strong>Company (Exhibitor)</strong>
              </TableCell>
              <TableCell>
                <strong>Selected Supplier</strong>
              </TableCell>
              <TableCell>
                <strong>Submitted On</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Action</strong>
              </TableCell>
            </MuiTableRow>
          </MuiTableHead>
          <MuiTableBody>
            {filteredData.length > 0 ? (
              filteredData.map((row) => (
                <MuiTableRow key={row._id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.exhibitorName}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {row.selectedSupplier?.supplierName || "N/A"}
                  </TableCell>
                  <TableCell>
                    {row.submittedOn
                      ? new Date(row.submittedOn).toLocaleString()
                      : "N/A"}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleOpenEmailDialog(row)}
                    >
                      View Email
                    </Button>
                  </TableCell>
                </MuiTableRow>
              ))
            ) : (
              <MuiTableRow>
                <TableCell colSpan={5} align="center">
                  <Typography py={2} color="textSecondary">
                    No submissions found.
                  </Typography>
                </TableCell>
              </MuiTableRow>
            )}
          </MuiTableBody>
        </MuiTable>
      </TableContainer>

      {/* Analytics Modal Dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: "#f5f5f5",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Submission Analytics
          <IconButton onClick={() => setStatsDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4, backgroundColor: "#f9f9f9" }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Submissions by Exhibitor
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                    {Object.entries(analytics.exhibitorCounts || {}).map(
                      ([name, count]) => (
                        <Box
                          key={name}
                          display="flex"
                          justifyContent="space-between"
                          mb={1}
                        >
                          <Typography variant="body2">{name}</Typography>
                          <Chip
                            label={count}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      )
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="secondary">
                    Popular Suppliers Selected
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                    {Object.entries(analytics.supplierCounts || {}).map(
                      ([name, count]) => (
                        <Box
                          key={name}
                          display="flex"
                          justifyContent="space-between"
                          mb={1}
                        >
                          <Typography variant="body2">{name}</Typography>
                          <Chip label={count} size="small" color="secondary" />
                        </Box>
                      )
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Email View Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#f5f5f5", mb: 2 }}>
          Email Preview - {selectedSubmission?.name}
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#f4f4f4", p: 4 }}>
          {isHeaderFooterLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={4}
            >
              <CircularProgress />
            </Box>
          ) : selectedSubmission ? (
            <Box
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
              dangerouslySetInnerHTML={{
                __html: generateEmailPreview(
                  selectedSubmission,
                  headerFooterData?.headerUrl,
                  headerFooterData?.footerUrl
                ),
              }}
            />
          ) : (
            <MuPb />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEmailDialog}
            color="primary"
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OesExplorer;
